// ============================================
// LIGA AJUPAM - "Esta pantalla necesita recargarse" (v6.11.218)
// ============================================
// Desde el paso 2 de v6.11.213 la base no le muestra al acceso público el DNI,
// el teléfono ni la fecha de nacimiento de `profiles`. Un pedido que todavía los
// pida vuelve 401 "permission denied for table profiles" y, sin nada más, la
// pantalla queda vacía.
//
// Dos redes, una para cada versión:
//   - Versión ANTERIOR a v6.11.213 abierta en segundo plano: no se puede cambiar
//     su código; la recarga el service worker (public/sw.js, v6.11.218) y llega
//     acá con `?actualizada=1` → `consumeUpdatedMarker()` → aviso "Actualizamos
//     la app".
//   - Versión actual: no debería mandar esos pedidos (lo frena
//     `npm run audit:profile-columns`), pero si alguno se escapa, en vez de una
//     pantalla vacía aparece un aviso con botón "Recargar" (`AppUpdateBanner`).

/** Evento de window que muestra el aviso con botón "Recargar". */
export const STALE_PERMISSIONS_EVENT = 'app-stale-permissions';

/** Parámetro con el que el service worker recarga una ventana vieja. */
export const UPDATED_MARKER_PARAM = 'actualizada';

/**
 * ¿La respuesta es el rechazo del paso 2? Sólo cuenta el de `profiles` (o la
 * función de sesiones vieja): otro 401 (token vencido, etc.) no es "recargá".
 */
export const isProfilesPermissionDenied = (status: number, body: string): boolean =>
  (status === 401 || status === 403) &&
  /permission denied for (table profiles|function create_user_session)/i.test(body);

/**
 * `fetch` para el cliente de Supabase: deja pasar todo tal cual y, si un pedido
 * a la base vuelve rechazado por el paso 2, avisa para mostrar "Recargar".
 */
export const fetchWithStaleGuard: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401 || response.status === 403) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (url.includes('/rest/v1/')) {
      response
        .clone()
        .text()
        .then((body) => {
          if (isProfilesPermissionDenied(response.status, body)) {
            window.dispatchEvent(new CustomEvent(STALE_PERMISSIONS_EVENT));
          }
        })
        .catch(() => { /* sin cuerpo legible: no se avisa */ });
    }
  }
  return response;
};

let updatedMarkerSeen = false;

/**
 * Al arrancar: si el service worker recargó esta ventana (`?actualizada=1`), lo
 * anota y saca el parámetro de la URL antes de que la app la lea.
 */
export const consumeUpdatedMarker = (): void => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(UPDATED_MARKER_PARAM) !== '1') return;
    url.searchParams.delete(UPDATED_MARKER_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    updatedMarkerSeen = true;
  } catch {
    /* URL rara: no hay nada que anotar */
  }
};

/** ¿Esta carga viene de una recarga automática del service worker? */
export const wasUpdatedBySw = (): boolean => updatedMarkerSeen;
